import navStyles from './CaseNav.module.css';

import { Fragment, JSX, useState } from 'react';
import { RiMenuLine } from 'react-icons/ri';

import { logTrace } from '../utils/trackingUtils';
import logo from '../assets/logo.png';
import logoMobile from '../assets/logo-mobile.png';
import { CaseReportSummary } from '../models/CaseTypes';
import { formatCaseDateDisplay } from '../utils/caseUtils';

interface CaseListProps {
  error: string | null;
  onLogout: () => void;
  onCaseSelect: (caseReport: CaseReportSummary) => void;
  onReload: () => void;
  onSortCases: (field: string) => void;
  sortedCases: CaseReportSummary[]|null;
  sortField?: string;
  sortDirection?: string;
}

const CaseNav = (
  { error, onLogout, onCaseSelect, onReload, onSortCases, sortedCases, sortField, sortDirection }: CaseListProps
): JSX.Element => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [selectedCase, setSelectedCase] = useState<CaseReportSummary | null>(null);

  const handleSort = (field: string): void => {
    onSortCases(field);
  };

  const handleCaseSelect = async (caseItem: CaseReportSummary): Promise<void> => {
    logTrace('CaseDetail', 'proprio_case_selected', caseItem.name);

    setSelectedCase(caseItem);
    onCaseSelect(caseItem);
  };

  const handleLogout = async (): Promise<void> => {
    onLogout();
  };

  document.documentElement.style.setProperty('--mobile-logo', `url(${logoMobile})`);

  return (
    <Fragment>
      {/* Navigation Bar */}
      <nav className={navStyles['navigation-bar']}>
        <div className={navStyles['nav-content']}>
          <div className={navStyles['menu-button']}>
            <button
              className={navStyles['drawer-toggle']}
              onClick={() => setIsDrawerOpen(!isDrawerOpen)}
              aria-label="Toggle navigation"
            >
              <RiMenuLine size={24} />
            </button>
          </div>
          <div className={navStyles['nav-links']}>
            <img src={logo} alt="Proprio Logo" className={navStyles['logo']} />
          </div>
          <div className={navStyles['user-profile']}>
            <button onClick={handleLogout}>Sign Out</button>
          </div>
        </div>
      </nav>

      {/* Navigation Drawer - Add this after the navigation bar */}
      <div className={`${navStyles['navigation-drawer']} ${isDrawerOpen ? navStyles['drawer-open'] : ''}`}>
        <div className={navStyles['drawer-content']}>
          <div className={navStyles['drawer-header']}>
            <h3 className={navStyles['case-history-title']}>Case History</h3>
            <button
              className={navStyles['drawer-close']}
              onClick={() => setIsDrawerOpen(false)}
              aria-label="Close drawer"
            >
              <span className={navStyles['close-icon']}>×</span>
            </button>
          </div>

          <div data-testid="drawer-body">
            <p className="case-history-description">
              Choose a case from your history to view the related case performance report.
            </p>

            {/* Column Headers */}
            <div className={navStyles['case-list-column-headers']}>
              <div className={navStyles['case-column']}>
                <div
                  className={navStyles['column-header-text']}
                  onClick={() => handleSort('name')}
                >
                  Case Name {sortField === 'name' && (sortDirection === 'asc' ? '▲' : '▼')}
                </div>
              </div>
              <div className={navStyles['date-column']}>
                <div
                  className={navStyles['column-header-text']}
                  onClick={() => handleSort('date')}
                >
                  Date {sortField === 'date' && (sortDirection === 'asc' ? '▲' : '▼')}
                </div>
              </div>
              <div className={navStyles['levels-column']}>
                <div
                  className={navStyles['column-header-text']}
                  onClick={() => handleSort('levels')}
                >
                  Levels {sortField === 'levels' && (sortDirection === 'asc' ? '▲' : '▼')}
                </div>
              </div>
            </div>

            {/* Case List */}
            <div data-testid="drawer-case-list">
              {error && (
                <div className="error-message">
                  {error}
                  <button onClick={onReload}>Retry</button>
                </div>
              )}

              {!sortedCases ? (
                <div className="loading-state">Unable to load cases.</div>
              ) :

                sortedCases.length === 0 ? (
                  <div className="empty-state">No cases found</div>
                ) : (
                  sortedCases.map((caseItem: CaseReportSummary) => (
                    <div
                      key={caseItem.id}
                      className={`${navStyles['case-item']} ${selectedCase && selectedCase.id === caseItem.id ? navStyles['case-item-selected'] : ''}`}
                      onClick={() => {
                        handleCaseSelect(caseItem);
                        setIsDrawerOpen(false); // Close drawer after selection on mobile
                      }}
                    >
                      {selectedCase && selectedCase.id === caseItem.id && (
                        <div className={navStyles['case-item-indicator']}></div>
                      )}
                      <div className={navStyles['case-name-cell']}>
                        <div className={navStyles['case-cell-content']}>
                          <span className="case-name-text">{caseItem.name}</span>
                        </div>
                      </div>
                      <div className={navStyles['case-date-cell']}>
                        <div className={navStyles['case-cell-content']}>
                          <span className={navStyles['case-date-text']}>{formatCaseDateDisplay(caseItem.date)}</span>
                        </div>
                      </div>
                      <div className={navStyles['case-level-cell']}>
                        <div className={navStyles['case-cell-content']}>
                          <span className={navStyles['case-level-text']}>{caseItem.levels || '-'}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
            </div>
          </div>
        </div>
      </div>
    </Fragment>

  );
};
export default CaseNav;
